import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useV2Auth } from '@/context/V2AuthContext';
import { useTranslation } from 'react-i18next';
import { Heart, Send, Sparkles, Eye, EyeOff, X, ChevronDown, ChevronUp, Pin } from 'lucide-react';
import { toast } from 'sonner';

export default function GratitudeV2() {
  const { user, API_V2 } = useV2Auth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [text, setText] = useState('');
  const [prefix, setPrefix] = useState('für');
  const [selectedTags, setSelectedTags] = useState([]);
  const [customTag, setCustomTag] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [status, setStatus] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [quickTags, setQuickTags] = useState([]);
  const [feed, setFeed] = useState([]);
  const [showFeed, setShowFeed] = useState(true);
  const [pinnedIds, setPinnedIds] = useState([]);

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    async function load() {
      try {
        const [statusRes, sugRes, tagsRes, feedRes, pinsRes] = await Promise.all([
          axios.get(`${API_V2}/gratitude/my-status`, { withCredentials: true }),
          axios.get(`${API_V2}/gratitude/suggestions`),
          axios.get(`${API_V2}/gratitude/tags`),
          axios.get(`${API_V2}/gratitude/feed`, { withCredentials: true }),
          axios.get(`${API_V2}/profile/pins`, { withCredentials: true }).catch(() => ({ data: { pinnedImpulses: [] } })),
        ]);
        setStatus(statusRes.data);
        setSuggestions(sugRes.data.suggestions);
        setQuickTags(tagsRes.data.tags);
        setFeed(feedRes.data);
        setPinnedIds((pinsRes.data.pinnedImpulses || []).map((p) => p.id));
      } catch (err) {
        if (err.response?.status === 401) navigate('/auth', { replace: true });
      }
    }
    load();
  }, [user, API_V2, navigate]);

  const pickSuggestion = (s) => {
    setText(s);
    setError(null);
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : prev.length < 5 ? [...prev, tag] : prev
    );
  };

  const addCustomTag = () => {
    const cleaned = customTag.trim().slice(0, 30);
    if (cleaned && !selectedTags.includes(cleaned) && selectedTags.length < 5) {
      setSelectedTags(prev => [...prev, cleaned]);
      setCustomTag('');
    }
  };

  const submit = async () => {
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(`${API_V2}/gratitude`, {
        text: text.trim(),
        prefix,
        tags: selectedTags,
        anonymous,
      }, { withCredentials: true });
      setSuccess(true);
      setText('');
      setSelectedTags([]);
      setAnonymous(false);
      if (status) setStatus({ ...status, remaining: res.data.remaining, todayCount: status.todayCount + 1 });
      const feedRes = await axios.get(`${API_V2}/gratitude/feed`, { withCredentials: true });
      setFeed(feedRes.data);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      if (err.response?.status === 401) { navigate('/auth', { replace: true }); return; }
      setError(err.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSubmitting(false);
    }
  };

  const togglePinImpulse = async (impulseId) => {
    try {
      if (pinnedIds.includes(impulseId)) {
        await axios.delete(`${API_V2}/profile/pin-impulse/${impulseId}`, { withCredentials: true });
        setPinnedIds(prev => prev.filter(x => x !== impulseId));
        toast.success(t('innerstes.unpinSuccess'));
      } else {
        await axios.post(`${API_V2}/profile/pin-impulse`, { id: impulseId, type: 'gratitude' }, { withCredentials: true });
        setPinnedIds(prev => [...prev, impulseId]);
        toast.success(t('innerstes.pinSuccess'));
      }
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen text-[var(--text)]">
      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-8 space-y-6">

        {/* Form */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 sm:p-8 shadow-sm space-y-5" data-testid="gratitude-form">
          <div className="text-center space-y-2">
            <div className="w-11 h-11 mx-auto rounded-full bg-[var(--primary)]/10 flex items-center justify-center">
              <Heart size={20} className="text-[var(--primary)]" />
            </div>
            <h1 className="text-lg sm:text-xl font-semibold">{t('gratitude.title', 'Wofür bist Du gerade dankbar?')}</h1>
            <p className="text-xs text-[var(--muted)]">{t('gratitude.subtitle', 'Teile Deine Dankbarkeit mit der Community.')}</p>
          </div>

          {/* Status badge */}
          {status && (
            <div className="space-y-2">
              <div className="flex items-center justify-center gap-3 text-xs text-[var(--muted)]" data-testid="gratitude-status">
                <span className="px-2.5 py-1 rounded-full bg-[var(--primary)]/8 text-[var(--primary)] font-medium">{status.level}</span>
                <span>{status.remaining} von {status.dailyLimit} heute übrig</span>
              </div>
              <p className="text-center text-[10px] text-[var(--muted)] max-w-md mx-auto" data-testid="gratitude-level-hint">
                {t('gratitude.levelHint')}
              </p>
            </div>
          )}

          {/* Clickable Suggestions */}
          {suggestions.length > 0 && (
            <div className="space-y-2" data-testid="gratitude-suggestions">
              <p className="text-xs text-[var(--muted)] font-medium">{t('gratitude.suggestionsLabel', 'Oder wähle einen Vorschlag:')}</p>
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => pickSuggestion(s)}
                    data-testid={`suggestion-${s.replace(/\s/g, '-').slice(0, 20)}`}
                    className={`px-3 py-1.5 rounded-full text-xs transition-all border ${
                      text === s
                        ? 'bg-[var(--primary)] text-white border-[var(--primary)]'
                        : 'bg-white border-[var(--border)] text-[var(--text)] hover:border-[var(--primary)]/40 hover:bg-[var(--primary)]/5'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Text area with prefix selector */}
          <div className="space-y-1">
            <div className="rounded-xl border border-[var(--border)] bg-white/70 overflow-hidden">
              <div className="px-4 pt-3 flex items-center gap-2">
                <span className="text-sm font-medium text-[var(--primary)]">Ich bin dankbar</span>
                <div className="flex rounded-full border border-[var(--border)] overflow-hidden text-xs">
                  <button
                    type="button"
                    onClick={() => setPrefix('für')}
                    data-testid="prefix-fuer"
                    className={`px-3 py-1 transition-all ${prefix === 'für' ? 'bg-[var(--primary)] text-white' : 'bg-white text-[var(--muted)] hover:bg-gray-50'}`}
                  >für...</button>
                  <button
                    type="button"
                    onClick={() => setPrefix('dafür, dass')}
                    data-testid="prefix-dafuer"
                    className={`px-3 py-1 transition-all ${prefix === 'dafür, dass' ? 'bg-[var(--primary)] text-white' : 'bg-white text-[var(--muted)] hover:bg-gray-50'}`}
                  >dafür, dass...</button>
                </div>
              </div>
              <textarea
                value={text}
                onChange={e => { setText(e.target.value); setError(null); }}
                placeholder={prefix === 'für' ? t('gratitude.placeholder', 'meine tollen Gespräche mit meinen Kollegen') : t('gratitude.placeholderDass', 'jemand heute an mich gedacht hat')}
                maxLength={280}
                rows={2}
                data-testid="gratitude-text-input"
                className="w-full bg-transparent px-4 pb-3 pt-1 text-sm resize-none focus:outline-none placeholder:text-[var(--muted)]"
              />
            </div>
            <div className="text-right text-[10px] text-[var(--muted)]">{text.length}/280</div>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <p className="text-xs text-[var(--muted)] font-medium">{t('gratitude.tagsLabel', 'Tags (optional)')}</p>
            <div className="flex flex-wrap gap-1.5">
              {quickTags.map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  data-testid={`tag-${tag.replace(/\s/g, '-')}`}
                  className={`px-2.5 py-1 rounded-full text-xs transition-all ${
                    selectedTags.includes(tag)
                      ? 'bg-[var(--primary)] text-white'
                      : 'bg-gray-100 text-[var(--muted)] hover:bg-gray-200'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <input
                type="text"
                value={customTag}
                onChange={e => setCustomTag(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addCustomTag()}
                placeholder={t('gratitude.customTag', 'Eigenen Tag hinzufügen...')}
                maxLength={30}
                data-testid="gratitude-custom-tag-input"
                className="flex-1 rounded-lg border border-[var(--border)] bg-white/70 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/30 placeholder:text-[var(--muted)]"
              />
              <button type="button" onClick={addCustomTag} disabled={!customTag.trim()} className="px-3 py-1.5 rounded-lg text-xs bg-gray-100 hover:bg-gray-200 disabled:opacity-40">+</button>
            </div>
            {selectedTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {selectedTags.map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-[var(--primary)]/10 text-[var(--primary)]">
                    {tag}
                    <button type="button" onClick={() => setSelectedTags(prev => prev.filter(t => t !== tag))} className="hover:text-red-500"><X size={10} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Anonymous toggle */}
          <button
            type="button"
            onClick={() => setAnonymous(!anonymous)}
            data-testid="gratitude-anonymous-toggle"
            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs transition-all w-full ${
              anonymous ? 'bg-[var(--primary)]/10 text-[var(--primary)]' : 'bg-gray-50 text-[var(--muted)]'
            }`}
          >
            {anonymous ? <EyeOff size={14} /> : <Eye size={14} />}
            {anonymous
              ? t('gratitude.anonymousOn', 'Anonym — Dein Name wird nicht angezeigt')
              : t('gratitude.anonymousOff', 'Mit Deinem Namen sichtbar')
            }
          </button>

          {error && <div className="text-xs text-red-500 text-center" data-testid="gratitude-error">{error}</div>}
          {success && <div className="text-xs text-green-600 text-center" data-testid="gratitude-success">{t('gratitude.success', 'Dein Dankbarkeits-Impuls wurde geteilt!')}</div>}

          <button
            type="button"
            onClick={submit}
            disabled={submitting || !text.trim() || (status && status.remaining <= 0)}
            data-testid="gratitude-submit-btn"
            className="w-full flex items-center justify-center gap-2 rounded-full py-3 text-sm font-medium bg-[var(--primary)] text-[var(--primary-contrast)] hover:opacity-90 disabled:opacity-40 transition-all active:scale-[0.98]"
          >
            <Send size={15} />
            {submitting ? t('common.loading') : t('gratitude.submitBtn', 'Dankbarkeit teilen')}
          </button>
        </div>

        {/* Feed */}
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setShowFeed(!showFeed)}
            className="flex items-center gap-2 text-sm font-semibold bg-white/70 backdrop-blur-sm rounded-full px-4 py-1.5"
          >
            <Sparkles size={14} className="text-[var(--primary)]" />
            {t('gratitude.feedTitle', 'Dankbarkeits-Impulse der Community')}
            {showFeed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {showFeed && (
            <div className="space-y-2.5">
              {feed.length === 0 && (
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 text-center text-sm text-[var(--muted)]">
                  {t('gratitude.emptyFeed', 'Noch keine Impulse. Sei der/die Erste!')}
                </div>
              )}
              {feed.map(imp => (
                <div key={imp.id} className="bg-white/85 backdrop-blur-sm rounded-2xl p-4 sm:p-5 shadow-sm" data-testid={`gratitude-item-${imp.id}`}>
                  <p className="text-sm leading-relaxed text-[var(--text)]"><span className="text-[var(--primary)] font-medium">Ich bin dankbar {imp.prefix || 'für'}</span> {imp.text}</p>
                  {imp.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {imp.tags.map(tag => (
                        <span key={tag} className="px-2 py-0.5 rounded-full text-[10px] bg-[var(--primary)]/8 text-[var(--primary)]">{tag}</span>
                      ))}
                    </div>
                  )}
                  <div className="mt-2.5 flex items-center justify-between">
                    <span className="text-[10px] text-[var(--muted)]">
                      {imp.author}
                      {imp.createdAt && ` · ${new Date(imp.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`}
                    </span>
                    {imp.isOwn && (
                      <button type="button" onClick={() => togglePinImpulse(imp.id)} data-testid={`pin-gratitude-btn-${imp.id}`} className={`inline-flex items-center gap-1 text-[10px] font-medium rounded-full px-2.5 py-1 border transition-colors ${pinnedIds.includes(imp.id) ? 'text-[var(--primary)] border-[var(--primary)]/50 bg-[var(--primary)]/10' : 'text-[var(--muted)] border-[var(--border)] hover:text-[var(--primary)]'}`}>
                        <Pin size={11} className={pinnedIds.includes(imp.id) ? 'fill-[var(--primary)]' : ''} />
                        {pinnedIds.includes(imp.id) ? t('innerstes.pinned') : t('innerstes.pin')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
